import { RefObject, useMemo } from 'react';

import { ExpandedState } from '@tanstack/react-table';

import {
  buildLastRowSectionMap,
  NestedTreeWalker,
  RegisterRowRef,
  SectionPaginationApi,
  useSectionIntersectionObservers,
} from '@/components/data-table/nested-pagination';

import { APPROVALS_PROJECTS_SECTION_ID } from '../constants/approvals-sections';
import {
  getApprovalRowId,
  getApprovalSubRows,
  isApprovalMemberRow,
  isApprovalProjectRow,
} from '../helpers/approval-row-model.helper';
import { ApprovalProjectRow, ApprovalTableRow } from '../types/time-tracking-approvals';

const LOG_TAG = 'ApprovalsNestedPagination:';

interface UseApprovalSectionObserverParams {
  /** The loaded tree WITHOUT sentinels, so loader rows are never observed as section tails. */
  enrichedData: ApprovalProjectRow[];
  expanded: ExpandedState;
  /** Inner-level (members/worklogs) pagination from `useLazyApprovalData`. */
  sectionPagination: SectionPaginationApi;
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  hasMoreProjects: boolean;
  isFetchingMoreProjects: boolean;
  onLoadMoreProjects?: () => void;
}

/**
 * Wires all three levels of the approvals tree into ONE scroll observer: the inner levels come
 * from the generic nested-pagination walker, and the top-level projects list — the tree's roots,
 * which the walker can't model — is registered under a reserved synthetic section id.
 *
 * Returns the `registerRowRef` the table attaches to every row.
 */
export const useApprovalSectionObserver = ({
  enrichedData,
  expanded,
  sectionPagination,
  scrollContainerRef,
  hasMoreProjects,
  isFetchingMoreProjects,
  onLoadMoreProjects,
}: UseApprovalSectionObserverParams): { registerRowRef: RegisterRowRef } => {
  // The inner-level API plus the roots under the reserved id. Identity-stable, so the observer is
  // never torn down just because a render produced a new object.
  const combinedPagination = useMemo<SectionPaginationApi>(
    () => ({
      getSection: id =>
        id === APPROVALS_PROJECTS_SECTION_ID
          ? { hasNextPage: hasMoreProjects, isFetchingNextPage: isFetchingMoreProjects }
          : sectionPagination.getSection(id),
      fetchNextPage: id => {
        if (id === APPROVALS_PROJECTS_SECTION_ID) {
          onLoadMoreProjects?.();
        } else {
          sectionPagination.fetchNextPage(id);
        }
      },
    }),
    [sectionPagination, hasMoreProjects, isFetchingMoreProjects, onLoadMoreProjects]
  );

  // Describes the rendered tree to the generic walker: projects and members are paginating
  // sections, worklog entries are leaves.
  const walker = useMemo<NestedTreeWalker<ApprovalTableRow>>(
    () => ({
      roots: enrichedData,
      getRowId: getApprovalRowId,
      getChildren: node => getApprovalSubRows(node) ?? [],
      isPaginatedSection: node => isApprovalProjectRow(node) || isApprovalMemberRow(node),
      isExpanded: rowId =>
        expanded === true || (typeof expanded === 'object' && expanded[rowId] === true),
    }),
    [enrichedData, expanded]
  );

  // Each section's last loaded row → its section, so the observer watches only section tails
  // (never rows below a section → no false triggers). The roots' tail is appended here because
  // the root list is a screen concern the generic walker doesn't model.
  const lastRowToSection = useMemo(() => {
    const map = buildLastRowSectionMap(walker, combinedPagination);
    if (hasMoreProjects && enrichedData.length > 0) {
      const lastProject = enrichedData[enrichedData.length - 1];
      map.set(getApprovalRowId(lastProject), { sectionId: APPROVALS_PROJECTS_SECTION_ID });
    }
    return map;
  }, [walker, combinedPagination, hasMoreProjects, enrichedData]);

  return useSectionIntersectionObservers({
    scrollContainerRef,
    lastRowToSection,
    sectionPagination: combinedPagination,
    logTag: LOG_TAG,
  });
};
